<script setup>
import { ref, computed, watch } from 'vue';
import Combobox from "@/components/ui/Combobox.vue";
import { Search, X } from "@lucide/vue"
import PlacesAutocomplete from "@/components/atoms/map/PlacesAutocomplete.vue";
import UserSearch from "@/components/user/UserSearch.vue";

const props = defineProps({
  exponents: { type: Array, required: true },
  userName: { type: String, required: true },
  countries: { type: Array, required: true },
  submitUrl: { type: String, required: true },
  csrfToken: { type: String, required: true },
  allowUserSearch: { type: Boolean, default: false },
  userSearchUrl: { type: String, default: null },
});

const selectedUser = ref(null);

function onUserSelected(user) {
  selectedUser.value = user;
  recipientName.value = `${user.firstname} ${user.lastname}`;
}

function clearSelectedUser() {
  selectedUser.value = null;
  recipientName.value = props.userName;
}

const exponentOptions = computed(() =>
  props.exponents.map((exponent) => ({ value: exponent.id, label: exponent.name }))
);

const exponentId = ref(props.exponents.length === 1 ? props.exponents[0].id : '');
const quantity = ref(null);
const companyName = ref(props.exponents.length === 1 ? props.exponents[0].name : '');
const recipientName = ref(props.userName);
const address = ref('');
const country = ref('');

const roundedQuantity = computed(() => {
  if (!quantity.value || quantity.value <= 0) {
    return 0;
  }
  return Math.ceil(quantity.value / 10) * 10;
});

watch(exponentId, (newId) => {
  const exponent = props.exponents.find((e) => e.id === newId);
  if (exponent) {
    companyName.value = exponent.name;
  }
});

function onPlaceChanged(place) {
  if (place.formatted_address) {
    address.value = place.formatted_address;
  }

  if (place.address_components) {
    const countryComponent = place.address_components.find(
      (c) => c.types.includes('country')
    );
    if (countryComponent) {
      country.value = countryComponent.short_name;
    }
  }
}
</script>
<template>
    <form :action="submitUrl" method="post">
        <input type="hidden" name="_token" :value="csrfToken">

        <div class="row">
            <div class="col-md-6">
                <div class="card mb-3">
                    <div class="card-body">
                        <h5 class="card-title">{{ $t('Order Details') }}</h5>

                        <div class="form-group mb-3">
                            <label for="exponent_id">{{ $t('Exponent') }}</label>
                            <Combobox
                                id="exponent_id"
                                name="exponent_id"
                                :options="exponentOptions"
                                :fullwidth="true"
                                v-model="exponentId"
                            />
                        </div>

                        <div v-if="allowUserSearch" class="form-group mb-3">
                            <label>{{ $t('Order on behalf of') }}</label>
                            <div v-if="selectedUser" class="tw:flex tw:items-center tw:gap-2">
                              <input type="hidden" name="user_id" :value="selectedUser.id">
                              <span class="form-control tw:bg-zinc-50" readonly>
                                {{ selectedUser.firstname }} {{ selectedUser.lastname }} ({{ selectedUser.email }})
                              </span>
                              <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearSelectedUser">
                                <X class="tw:size-4" />
                              </button>
                            </div>
                            <UserSearch
                              v-else
                              :search-url="userSearchUrl"
                              input-id="barcode-order-user-search"
                              :placeholder="$t('Search by name or email...')"
                              @select="onUserSelected"
                            />
                            <small class="text-muted">
                              {{ $t('Leave empty to create the order for yourself.') }}
                            </small>
                        </div>

                        <div class="form-group mb-3">
                            <label for="quantity_requested">{{ $t('Quantity') }}</label>
                            <input
                                id="quantity_requested"
                                type="number"
                                name="quantity_requested"
                                class="form-control"
                                v-model.number="quantity"
                                min="1"
                                required
                            >
                            <small v-if="roundedQuantity > 0" class="text-muted">
                                {{ $t('Will be rounded up to') }} <strong>{{ roundedQuantity }}</strong> {{ $t('barcodes') }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md-6">
                <div class="card mb-3">
                    <div class="card-body">
                        <h5 class="card-title">{{ $t('Delivery Address') }}</h5>

                        <div class="form-group mb-3">
                            <label for="company_name">{{ $t('Company Name') }}</label>
                            <input
                                id="company_name"
                                type="text"
                                name="company_name"
                                class="form-control"
                                v-model="companyName"
                                required
                            >
                        </div>

                        <div class="form-group mb-3">
                            <label for="recipient_name">{{ $t('Recipient Name') }}</label>
                            <input
                                id="recipient_name"
                                type="text"
                                name="recipient_name"
                                class="form-control"
                                v-model="recipientName"
                                required
                            >
                        </div>

                        <div class="form-group mb-3">
                            <label for="address_search">{{ $t('Search Address') }}</label>
                            <div class="input-group mb-2 mr-sm-2">
                              <div class="input-group-prepend">
                                <div class="input-group-text">
                                  <Search class="tw:size-3"></Search>
                                </div>
                              </div>
                              <PlacesAutocomplete
                                id="address_search"
                                class="form-control"
                                @place-changed="onPlaceChanged"
                              />
                            </div>
                        </div>

                        <div class="form-group mb-3">
                            <label for="address">{{ $t('Full Address') }}</label>
                            <textarea
                                id="address"
                                name="address"
                                class="form-control"
                                rows="3"
                                v-model="address"
                                required
                            ></textarea>
                        </div>

                        <div class="form-group mb-3">
                            <label for="country">{{ $t('Country') }}</label>
                            <Combobox
                                id="country"
                                name="country"
                                :options="countries"
                                :fullwidth="true"
                                v-model="country"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="form-group">
            <button type="submit" class="btn btn-primary">{{ $t('Order Barcode Labels') }}</button>
        </div>
    </form>
</template>
